<script>
    import { params, url, page } from "@roxi/routify";
    import { formattedDateAndTime } from "$/utils/date";
    import { exportTopicToCsv } from "$/utils/CsvExporter";
    import { clickAction } from "$/utils/clickAction";
    import { clickOutside } from "$/utils/clickOutside";
    import SVGIcon from "$/components/SVGIcon.svelte";
    import BottomPopUpModal from "$/components/modals/BottomPopUpModal.svelte";
    import IntroModal from "$/components/modals/IntroModal/IntroModal.svelte";
    import { TokenStore } from "$/stores/TokenStore";
    import { TenantStore } from "$/stores/TenantStore";
    import {AppStore, Panels} from "$/stores/AppStore";
    import {
        ActiveTopicStore,
        ActiveTopic,
        TopicAccessLevel,
    } from "$/stores/extension/ActiveTopicStore";
    import { TopicViewStore, TopicViewTypes } from "$/stores/TopicViewStore";
    import { tooltip } from "$/utils/tooltip";
    import { SelectedSnippetsStore } from "$/stores/SelectedSnippetsStore";
    import { BottomPopUpModalAction } from "$/components/modals/BottomPopUpModalAction.js";
    import DashboardViewManager from "$/components/DashboardViewManager/DashboardViewManager.svelte";
    import {
        getSnippetUrlPreview,
        copyToClip,
        PAGE_WRAPPER_ID,
        showtToastMessage,
        clearUrlByMarker,
    } from "$/utils/Utils";
    import { ClusterStore } from "$/stores/ClusterStore";
    import ArchivedSnippetsPanel from "$/components/ArchivedSnippetsPanel.svelte";
    import SidePanel from "$/components/SidePanel.svelte";

    let showMoreOptionsMenu = false;
    let oldParamSnippetId;
    let oldTenantId;

    $: currentTenantId = $TenantStore?.active?.id;

    $: if ($params.id) {
        // focus on object
        if ($params.type === "COMMENT") {
            AppStore.setFocused({ commentId: $params.id });
        } else {
            AppStore.setFocused({ snippetId: $params.id });
        }

        clearUrlByMarker("?");
    }

    $: (async () => {
        // HACK: _layout.svelte clears the store on URL changes, which causes the ActiveTopic
        // state to reset when a user is on the inbox page and opens a snippet (via search)
        // that belongs to the inbox. To handle this, we reload the inbox, but this results in the inbox
        // being loaded twice: once when the URL changes to /inbox?id=ID,
        // and again when the snippet is highlighted and the id param is removed from the URL.
        if (
            currentTenantId !== oldTenantId ||
            ($params && $params.id !== oldParamSnippetId)
        ) {
            oldParamSnippetId = $params.id;
            oldTenantId = currentTenantId;
            await ActiveTopicStore.init(
                null,
                TokenStore,
                TenantStore,
                ClusterStore,
            );
        }
    })();

    $: selectedView = $TopicViewStore
        ? TopicViewStore.selectedViewByTopicId(null)
        : TopicViewTypes.BOARD;

    function generateTopicMarkdown() {
        let markdown = "";
        markdown = `<h1>${$ActiveTopic.name}</h1>`;
        $ActiveTopic.snippets.forEach((snippet, index) => {
            if (index > 0) {
                markdown += `<hr/>`;
            }
            markdown += `<h3>Snippet - <a href="${snippet.url}">[${getSnippetUrlPreview(snippet.url)}]</a></h3>`;
            
            if (snippet.name) {
                markdown += `<h4>${snippet.name}</h4>`;
            }
            
            markdown += `<mark class="highlight-pink_background">${snippet.content}</mark>`;
            if (snippet.comments && snippet.comments.length > 0) {
                markdown += `<h4>Comments</h4>`;
                if (snippet.comments) {
                    markdown += `<ul>`;
                    snippet.comments.forEach((comment) => {
                        markdown += `<li><em><strong>${comment.createdBy?.firstName} ${comment.createdBy?.lastName}</strong>, ${formattedDateAndTime(comment.createdOn)}</em> <blockquote>${comment.comment}</blockquote></li>`;
                    });
                    markdown += `</ul>`;
                }
            }
        });
        copyToClip(markdown);

        showtToastMessage("Topic copied to clipboard");
    }

    function downloadTopicAsCSV() {
        exportTopicToCsv($ActiveTopic, $ActiveTopic.name.trim());
    }

    // start of selected snippets count texts
    let initialText;
    let deleteConfirmationText;
    let deleteModalAction = new BottomPopUpModalAction(
        async () => {
            await deleteModalAction.deleteSelectedSnippets(
                $SelectedSnippetsStore,
            );
        },
        async () => {
            SelectedSnippetsStore.clear();
        },
        "Delete",
        "Deleting snippets...",
        "pink",
    );

    $: {
        initialText = `<strong>${$SelectedSnippetsStore.length} ${$SelectedSnippetsStore.length > 1 ? " snippets" : " snippet"}</strong> selected`;
        deleteConfirmationText =
            `Delete <strong>${$SelectedSnippetsStore.length}` +
            `${$SelectedSnippetsStore.length > 1 ? " snippets" : " snippet"}</strong>?`;
    }

    // end of selected snippets count texts

    function setView(id, view) {
        let currentData = $TopicViewStore;

        const index = currentData.findIndex((item) => item.id === id);

        if (index !== -1) {
            currentData[index].view = currentData[index].view = view;
        } else {
            currentData.push({ id, view: view });
        }

        TopicViewStore.set(currentData);
    }
</script>

{#key $page.path}
    <IntroModal />

    {#if $SelectedSnippetsStore.length > 0}
        <BottomPopUpModal
            actions={[deleteModalAction]}
            {initialText}
            confirmationTexts={[deleteConfirmationText]}
        />
    {/if}

    <div id={PAGE_WRAPPER_ID} class="topic-page-wrapper">
        <div class="topic-header">
            <!-- Topic Header -->
            <div class="topic-header__info">
                <span class="topic-header__title">Inbox</span>

                <div class="topic-header__info-footer">
                    <span>Cool stuff that you saved for later 🫶</span>
                </div>
            </div>

            <!-- Topic Actions -->
            {#if $ActiveTopic}
                <div class="topic-header__option-wrapper">
                    <div class="hidden md:block">
                        <!-- show only on large screens -->
                        {#if selectedView === TopicViewTypes.GRID}
                            <button
                                class="topic-header__option-button"
                                use:tooltip={"Board view"}
                                on:click={() =>
                                    setView(null, TopicViewTypes.BOARD)}
                            >
                                <SVGIcon name="columns" size={20} />
                            </button>
                        {:else}
                            <button
                                class="topic-header__option-button"
                                use:tooltip={"Grid view"}
                                on:click={() =>
                                    setView(null, TopicViewTypes.GRID)}
                            >
                                <SVGIcon name="board" size={20} />
                            </button>
                        {/if}
                    </div>

                    <div class="relative">
                        <button
                            class="topic-header__option-button"
                            class:bg-gray-100={showMoreOptionsMenu}
                            class:rounded-b-none={showMoreOptionsMenu}
                            use:tooltip={"More options"}
                            on:click={() => {
                                showMoreOptionsMenu = !showMoreOptionsMenu;
                            }}
                        >
                            <SVGIcon name="ellipsis-horizontal" size="20" />
                        </button>

                        {#if showMoreOptionsMenu}
                            <div
                                use:clickOutside
                                on:click_outside={() =>
                                    (showMoreOptionsMenu = false)}
                                class="topic-header__dropdown"
                            >
                                <div
                                    class="topic-header__dropdown-options-wrapper"
                                    role="menu"
                                    aria-orientation="vertical"
                                    aria-labelledby="options-menu"
                                >
                                    <div
                                        class="topic-header__dropdown-option"
                                        use:clickAction={() => {
                                            generateTopicMarkdown();
                                            showMoreOptionsMenu = false;
                                        }}
                                    >
                                        <div
                                            class="topic-header__option-button"
                                        >
                                            <SVGIcon
                                                name="clipboard-document-list"
                                                size="20"
                                            />
                                        </div>
                                        <div>Copy Snippets</div>
                                    </div>

                                    <div
                                        class="topic-header__dropdown-option"
                                        use:clickAction={() => {
                                            downloadTopicAsCSV();
                                            showMoreOptionsMenu = false;
                                        }}
                                    >
                                        <div
                                            class="topic-header__option-button"
                                        >
                                            <SVGIcon
                                                name="csv-document"
                                                size="20"
                                            />
                                        </div>
                                        <div>Download CSV</div>
                                    </div>

                                    <div
                                            class="topic-header__dropdown-option"
                                            use:clickAction={() => {
                                                AppStore.togglePanel(
                                                    Panels.SNIPPET_ARCHIVE,
                                                );
                                                showMoreOptionsMenu = false;
                                            }}
                                    >
                                        <div
                                                class="topic-header__option-button"
                                        >
                                            <SVGIcon
                                                    name="archive"
                                                    size="20"
                                            />
                                        </div>
                                        <div>Completed</div>
                                    </div>

                                </div>
                            </div>
                        {/if}
                    </div>
                </div>
            {/if}
        </div>
        <DashboardViewManager
            userTopicRole={TopicAccessLevel.OWNER}
            selectedTagNames={[]}
            user={$TokenStore.user}
            hideReactions={true}
        />

        <SidePanel show={$AppStore.panel === Panels.SNIPPET_ARCHIVE}>
            <ArchivedSnippetsPanel />
        </SidePanel>
    </div>
{/key}

<style>
    .topic-page-wrapper {
        @apply flex w-full flex-col overflow-hidden;
    }

    .topic-header {
        @apply flex items-center justify-between gap-1 p-5 shadow-[0_10px_10px_rgba(255,255,255,1)] md:p-[20px_20px_4px_20px];
    }

    .topic-header__info {
        @apply flex flex-1 flex-col;
    }

    .topic-header__info-footer {
        @apply flex w-fit flex-row items-center gap-1 text-xs;
    }

    .topic-header__title {
        @apply text-xl font-semibold;
    }

    .topic-header__option-wrapper {
        @apply flex flex-wrap items-center justify-between;
    }

    .topic-header__dropdown {
        @apply absolute right-0 z-[60] min-w-max rounded-md border border-solid border-gray-200 bg-white text-sm shadow-xl;
    }

    .topic-header__dropdown-options-wrapper {
        @apply p-1;
    }

    .topic-header__dropdown-option {
        @apply flex cursor-pointer items-center gap-1 overflow-hidden rounded pl-2 pr-4 hover:bg-gray-200;
    }

    .topic-header__option-button {
        @apply relative self-baseline rounded p-1 hover:bg-gray-200;
    }

    .topic-page-wrapper input {
        @apply w-full text-xl font-semibold outline-none;
    }
</style>
